import React from 'react';
import Button from '@mui/material/Button';
import { type CnTask } from './types';

interface TasksViewProps {
  tasks: CnTask[];
  onViewReport: (reportId: number) => void;
}

export default function TasksView({ tasks, onViewReport }: TasksViewProps) {
  const getTaskTypeLabel = (taskType: string) => {
    const labels: Record<string, string> = {
      resolve: 'Resolve Feedback',
      submit: 'Submit Report',
      publish: 'Publish Report',
      update: 'Update Report',
      interimUpdate: 'Interim Updates Needed',
      finalize: 'Final Updates Needed',
      allocate: 'Allocate Investment',
      add_programs: 'Add Your Organization\'s Programs',
      complete_profile: 'Complete Program Profile',
      create_report: 'Create a Report',
    };
    return labels[taskType] || taskType;
  };

  const onViewFeedback = (reportId: number) => {
    window.location.href = `/npo/feedback/${reportId}`;
  };

  return (
    <div className="cn-modal-overlay">
      <div className="cn-modal-content">
        <h2 className="cn-modal-title">Pending Tasks</h2>
        <p className="cn-modal-description">
          You have {tasks.length} pending {tasks.length === 1 ? 'task' : 'tasks'} that require your attention.
        </p>

        <div className="cn-tasks-container">
          {tasks.map((task) => (
            <div key={task.id} className="cn-task-card">
              <div className="cn-task-header">
                <h3 className="cn-task-type">{getTaskTypeLabel(task.task_type)}</h3>
                {task.overdue_days !== null && task.overdue_days > 0 && (
                  <span className="cn-task-overdue-badge">
                    {task.overdue_days} {task.overdue_days === 1 ? 'day' : 'days'} overdue
                  </span>
                )}
              </div>
              <div className="cn-task-details">
                {task.report_id !== null && (
                  <div className="cn-task-detail-row">
                    <span className="cn-task-label">Report:</span>
                    <span className="cn-task-value">
                      {task.report_name} ({task.report_year})
                    </span>
                  </div>
                )}
                {/* TIPD-4005 <div className="cn-task-detail-row">
                  <span className="cn-task-label">Due Date:</span>
                  <span className="cn-task-value">{formatDate(task.due_date)}</span>
                </div> */}
              </div>
              <div className="cn-task-actions">
                {task.task_type === 'resolve' && (
                  <Button
                    onClick={() => onViewFeedback(task.report_id!)}
                    variant="contained"
                    color="primary"
                    size="medium"
                    className="cn-task-view-button"
                  >
                    View Feedback
                  </Button>
                )}
                {task.task_type === 'add_programs' && (
                  <Button
                    onClick={() => { window.location.href = '/npo/programs-and-reports'; }}
                    variant="contained"
                    color="primary"
                    size="medium"
                    className="cn-task-view-button"
                  >
                    Add Programs
                  </Button>
                )}
                {task.task_type === 'complete_profile' && task.program_id != null && (
                  <Button
                    onClick={() => { window.location.href = `/npo/programs-and-reports?programId=${task.program_id}`; }}
                    variant="contained"
                    color="primary"
                    size="medium"
                    className="cn-task-view-button"
                  >
                    Complete Profile
                  </Button>
                )}
                {task.task_type === 'create_report' && (
                  <Button
                    onClick={() => { window.location.href = '/npo/programs-and-reports'; }}
                    variant="contained"
                    color="primary"
                    size="medium"
                    className="cn-task-view-button"
                  >
                    Add Report
                  </Button>
                )}
                {task.report_id !== null && (
                  <Button
                    onClick={() => onViewReport(task.report_id!)}
                    variant="contained"
                    color="primary"
                    size="medium"
                    className="cn-task-view-button"
                  >
                    Open Report
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <style {...({ jsx: true } as any)}>{`
        .cn-tasks-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
          max-height: 500px;
          overflow-y: auto;
        }

        .cn-task-card {
          background: #ffffff;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          transition: box-shadow 0.2s ease;
        }

        .cn-task-card:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .cn-task-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .cn-task-type {
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }

        .cn-task-overdue-badge {
          background: #f44336;
          color: #ffffff;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .cn-task-details {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
        }

        .cn-task-detail-row {
          display: flex;
          gap: 8px;
        }

        .cn-task-label {
          font-weight: 600;
          color: #666666;
          min-width: 80px;
        }

        .cn-task-value {
          color: #1a1a1a;
        }

        .cn-task-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }

        .cn-task-view-button {
          background-color: #003d7a !important;
          color: #ffffff !important;
          text-transform: none !important;
          font-weight: 600 !important;
          padding: 8px 24px !important;
          border-radius: 4px !important;
        }

        .cn-task-view-button:hover {
          background-color: #002a54 !important;
        }
      `}</style>
    </div>
  );
}
